import { useContext, useEffect, useMemo } from 'react';
import { isRunActivelySettling } from '../types/Run';
import { ReprocessContext } from '../providers/reprocessContext';

interface WatchableRun {
  id?: string | null;
  run_status?: number | null;
  processing_stage?: string | null;
}

// The batch progress endpoint rejects more than 200 ids outright, and a 400
// would stall the whole watch. Stay well under it: any overflow is simply left
// to the list's own poll, which skips watched runs and so covers the remainder.
const MAX_WATCHED = 100;

/**
 * Hand every run that is still working towards a terminal state to the
 * ReprocessProvider's `/runs/progress` watcher.
 *
 * Two populations, one mechanism:
 *  - a live reprocess (`processing_stage` populated) — reload recovery, as before
 *  - a run still on its first pass (uploaded / processing) — a fresh upload
 *
 * Planned (bare-`CREATED`) runs are excluded: they sit in the settling band by
 * design and never advance on their own, so watching them would poll forever
 * for nothing.
 *
 * The provider polls them on one batched request and invalidates the run and
 * session caches when they finish, so every list showing that run refreshes —
 * including the ones (SessionView, SessionSimple) that have no poll of their
 * own and would otherwise sit on a stale "Processing…" until a hard refresh.
 *
 * Safe to call from several mounted pages at once: watches are keyed by run id
 * and probe-mode registration never disturbs an in-flight one.
 */
export function useWatchSettlingRuns(runs: WatchableRun[] | null | undefined): void {
  const reprocess = useContext(ReprocessContext);
  const watchRuns = reprocess?.watchRuns;

  const ids = useMemo(
    () => (runs ?? [])
      .filter((r) => r && (r.processing_stage != null || isRunActivelySettling(r.run_status)))
      .map((r) => r.id)
      .filter((id): id is string => !!id)
      .slice(0, MAX_WATCHED)
      .join(','),
    [runs],
  );

  useEffect(() => {
    if (!watchRuns || !ids) return;
    watchRuns(ids.split(','));
  }, [ids, watchRuns]);
}

/** Flatten a session payload's configs into its runs, for `useWatchSettlingRuns`. */
export function sessionRuns<T = any>(session: { configs?: { runs?: T[] }[] } | null | undefined): T[] {
  return (session?.configs ?? []).flatMap((c) => c?.runs ?? []);
}
